// DipSiteRecorder — modal for recording a water-source location and
// (optionally) an immediate fill event. Opens from the long-press
// picker (and eventually from a dedicated "log dip" button on the
// flight detail page).
//
// Flow:
//   1. Pilot taps the location on the map; long-press picker shows
//      "Record dip site" alongside Save-waypoint / Add-to-plan.
//   2. This modal opens with lat/lon prefilled, name suggested.
//   3. Pilot picks source type, enters volume (gallons) + notes.
//   4. On Save: create dip_site row, record use, trigger landowner
//      lookup. Modal closes; toast shows "saved + owner classified
//      as <X>".
//
// The landowner lookup runs IN PARALLEL with the save so the user
// isn't blocked by the network — they see ownership info appear
// in the success toast or next time they open the dip site.

import { useState } from 'react';
import { X, Droplet, MapPin } from '../shell/Icon';
import {
  createDipSiteWithLookup,
  recordDipSiteUse,
  defaultDipSiteName,
  type DipSiteSourceType,
  type LandownerLookupResult,
} from '../lib/dipSites';
import { useAlertStore } from '../stores/alertStore';

interface Props {
  lat: number;
  lon: number;
  /** Optional flight context for the immediate fill event. */
  flightId?: string;
  flightClientId?: string;
  aircraftClientId?: string;
  incidentId?: string;
  onClose: () => void;
  /** Called once the dip site is saved (with the new id) so the host
   *  can refresh its map source / list. */
  onSaved?: (dipSiteId: string, owner?: LandownerLookupResult) => void;
}

const SOURCE_TYPES: ReadonlyArray<{ value: DipSiteSourceType; label: string }> = [
  { value: 'pond',       label: 'Pond' },
  { value: 'lake',       label: 'Lake' },
  { value: 'river',      label: 'River / stream' },
  { value: 'pool',       label: 'Swimming pool' },
  { value: 'hydrant',    label: 'Hydrant' },
  { value: 'tank_truck', label: 'Tank truck' },
  { value: 'other',      label: 'Other' },
];

export default function DipSiteRecorder({
  lat, lon,
  flightId, flightClientId, aircraftClientId, incidentId,
  onClose, onSaved,
}: Props) {
  const [name, setName]               = useState(() => defaultDipSiteName(lat, lon));
  const [sourceType, setSourceType]   = useState<DipSiteSourceType>('pond');
  const [gallons, setGallons]         = useState<string>('');
  const [notes, setNotes]             = useState<string>('');
  const [accessNotes, setAccessNotes] = useState<string>('');
  const [hazards, setHazards]         = useState<string>('');
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState<string | null>(null);

  const pushAlert = useAlertStore((s) => s.push);

  async function handleSave() {
    setError(null);
    setSaving(true);
    try {
      const created = await createDipSiteWithLookup({
        name: name.trim() || defaultDipSiteName(lat, lon),
        sourceType,
        latitude: lat,
        longitude: lon,
        accessNotes: accessNotes.trim() || undefined,
        hazards: hazards.trim() || undefined,
      });
      if (!created.ok || !created.id) {
        setError(created.error ?? 'Save failed');
        setSaving(false);
        return;
      }

      // If the pilot entered a gallons figure, also record an immediate
      // use event. (Zero gallons = "logged the source, not pumping
      // right now" — still useful as a known location.)
      const gal = Number(gallons);
      if (Number.isFinite(gal) && gal > 0) {
        await recordDipSiteUse({
          dipSiteId: created.id,
          gallons: Math.round(gal),
          suppressant: 'water',
          flightId,
          flightClientId,
          aircraftClientId,
          incidentId,
          notes: notes.trim() || undefined,
        });
      }

      const ownerLabel = created.owner
        ? (created.owner.ownershipClass === 'federal'
            ? `Federal land (${created.owner.managingAgency ?? 'unknown agency'})`
           : created.owner.ownerName
            ? `Private \u2014 ${created.owner.ownerName}`
           : created.owner.ownershipClass === 'private'
            ? 'Private land (owner lookup pending)'
            : 'Ownership unknown')
        : 'Ownership unknown';

      pushAlert({
        id: `dip-saved-${created.id}`,
        severity: 'info',
        title: 'Dip site recorded',
        body: `${name.trim() || 'Source'} \u00b7 ${ownerLabel}`,
        ttlMs: 8000,
      });

      onSaved?.(created.id, created.owner);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center"
      role="dialog"
      aria-label="Record dip site"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-cockpit-800 border border-cockpit-700 rounded-t-xl sm:rounded-xl shadow-rail w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
        <header className="flex items-center justify-between px-4 py-3 border-b border-cockpit-700 sticky top-0 bg-cockpit-800">
          <div className="flex items-center gap-2">
            <Droplet size={18} className="text-cyan-400" />
            <h2 className="text-sm font-semibold text-cockpit-100">Record Dip Site</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-cockpit-300 hover:text-cockpit-100"
          >
            <X size={18} />
          </button>
        </header>

        <div className="px-4 py-3 space-y-3">
          {/* Position readout */}
          <div className="flex items-center gap-2 text-xs text-cockpit-400">
            <MapPin size={14} className="text-cockpit-500" />
            <span className="font-mono">{lat.toFixed(5)}, {lon.toFixed(5)}</span>
          </div>

          {/* Name */}
          <Field label="Source name">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Smith Pond"
              className="w-full bg-cockpit-700 border border-cockpit-600 rounded px-2 py-1.5 text-sm text-cockpit-100"
            />
          </Field>

          {/* Source type */}
          <Field label="Source type">
            <select
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value as DipSiteSourceType)}
              className="w-full bg-cockpit-700 border border-cockpit-600 rounded px-2 py-1.5 text-sm text-cockpit-100"
            >
              {SOURCE_TYPES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </Field>

          {/* Gallons (optional — fill event) */}
          <Field
            label="Volume pulled (gallons, optional)"
            hint="Leave blank to log the source location only."
          >
            <input
              type="number"
              inputMode="numeric"
              min="0"
              step="1"
              value={gallons}
              onChange={(e) => setGallons(e.target.value)}
              placeholder="e.g. 800"
              className="w-full bg-cockpit-700 border border-cockpit-600 rounded px-2 py-1.5 text-sm font-mono text-cockpit-100"
            />
          </Field>

          {/* Notes for this specific fill */}
          <Field label="Notes (this fill)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. First drop \u2014 Canyon Fire, north flank"
              rows={2}
              className="w-full bg-cockpit-700 border border-cockpit-600 rounded px-2 py-1.5 text-sm text-cockpit-100 resize-none"
            />
          </Field>

          {/* Access + hazards (one-time, about the source) */}
          <Field label="Access notes (about this source)">
            <textarea
              value={accessNotes}
              onChange={(e) => setAccessNotes(e.target.value)}
              placeholder="e.g. Approach from north \u2014 power lines on south end"
              rows={2}
              className="w-full bg-cockpit-700 border border-cockpit-600 rounded px-2 py-1.5 text-sm text-cockpit-100 resize-none"
            />
          </Field>

          <Field label="Hazards">
            <textarea
              value={hazards}
              onChange={(e) => setHazards(e.target.value)}
              placeholder="e.g. Dead tree at north edge \u2014 watch rotor wash"
              rows={2}
              className="w-full bg-cockpit-700 border border-cockpit-600 rounded px-2 py-1.5 text-sm text-cockpit-100 resize-none"
            />
          </Field>

          <p className="text-[11px] text-cockpit-500 leading-relaxed">
            Landowner lookup runs automatically after save. Federal land
            → no reimbursement flagged. Private land → marked
            "pending" and queued for the org admin to follow up.
          </p>

          {error && (
            <div className="text-xs text-red-400 bg-red-900/30 border border-red-800 rounded px-2 py-1.5">
              {error}
            </div>
          )}
        </div>

        <footer className="px-4 py-3 border-t border-cockpit-700 flex items-center justify-end gap-2 sticky bottom-0 bg-cockpit-800">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-sm text-cockpit-300 hover:text-cockpit-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white rounded-md font-medium"
          >
            {saving ? 'Saving\u2026' : 'Save dip site'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({
  label, children, hint,
}: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-widest text-cockpit-400 uppercase mb-1">
        {label}
      </span>
      {children}
      {hint && <span className="block text-[10px] text-cockpit-500 mt-0.5">{hint}</span>}
    </label>
  );
}
